import React, { useState, useEffect } from "react";
import Datatable from "../../../components/shared/datatable/Datatable";
import { EditSvg } from "../../../svgFiles/EditSvg";
import { DeleteSvg } from "../../../svgFiles/DeleteSvg";
import { toast } from "react-hot-toast";
import {
  useRemoveManagerMutation,
  useSearchManagersQuery,
} from "./managersApi";
import ManagerModal from "./ManagerModal";
import CommonDeleteModal from "../../../components/shared/modalLayout/CommonDeleteModal";
import { PlusSvg } from "../../../svgFiles/PlusSvg";
import { convertToUserTimezone, showToast, ucwords } from "../../../helpers/Utils";
import useTranslator from "../../../hooks/useTranslator";

const Managers = () => {
  const { _t } = useTranslator();
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedManager, setSelectedManager] = useState(null);
  const [managerToDelete, setManagerToDelete] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const {
    data: managers,
    isLoading,
    isError,
    error,
  } = useSearchManagersQuery({ page, pageSize });
  const [removeManager, { isLoading: isDeleting }] = useRemoveManagerMutation();

  useEffect(() => {
    if (isError) {
      showToast(error.data.message || "Failed to fetch managers", "error");
    }
  }, [isError, error]);

  const handleAddManager = () => {
    setModalMode("add");
    setSelectedManager(null);
    setShowModal(true);
  };

  const handleEdit = (manager) => {
    setModalMode("edit");
    setSelectedManager(manager);
    setShowModal(true);
  };

  const handleDelete = (manager) => {
    setManagerToDelete(manager);
    setShowConfirmationModal(true);
  };

  const confirmDelete = async () => {
    setShowConfirmationModal(false);
    try {
      if (managerToDelete && managerToDelete.id) {
        await removeManager(managerToDelete.id).unwrap();
      } else {
        showToast("Please select a manager first.", "error");
      }
    } catch (error) {
      console.error("Delete Error:", error);
      showToast(
        error?.data?.message || "Error deleting manager. Please try again.",
        "error"
      );
    } finally {
      setManagerToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmationModal(false);
    setManagerToDelete(null);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  const columns = [
    { 
      label: _t("date", "Date"),
      accessor: "date_of_joining",
      cell: ({ value }) => value ? convertToUserTimezone(value) : "N/A" 
    },
    { label: _t("name", "Name"), accessor: "name", cell: ({ value }) => ucwords(value) ?? "N/A" },
    { label: _t("email", "Email"), accessor: "email" },
    { label: _t("phone_no", "Phone No"), accessor: "mobile", cell: ({ value }) => value ?? "N/A"  },
    { label: _t("role", "Role"), accessor: "role_name", cell: ({ value }) => ucwords(value) ?? "N/A"  },
    // { label: _t("address", "Address"), accessor: "address", cell: ({ value }) => value ?? "N/A"  },
    {
      label: _t("edit_delete", "Edit/Delete"),
      accessor: "actions",
      cell: ({ row }) => (
        <>
          <span className="cursor-pointer pe-2" onClick={() => handleEdit(row)}>
            <EditSvg />
          </span>
          <span className="cursor-pointer" onClick={() => handleDelete(row)}>
            {DeleteSvg}
          </span>
        </>
      ),
    },
  ];

  return (
    <div>
      <ManagerModal
        show={showModal}
        setShow={setShowModal}
        mode={modalMode}
        manager={selectedManager}
      />
      <CommonDeleteModal
        show={showConfirmationModal}
        handleClose={cancelDelete}
        title="Confirm Delete"
        message={`${_t(
          "sure_confirm_delete",
          "Are you sure you want to delete"
        )} ${managerToDelete?.name}?`}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
      />      
        <Datatable
          title={_t("managers", "Managers")}
          columns={columns}
          tableData={managers?.data}
          buttons={[
            {
              label: _t("add_manager", "Add Manager"),
              onClick: handleAddManager,
              icon: PlusSvg,
            },
          ]}
          pagination
          page={page}
          onPageChange={handlePageChange}
          isLoading={isLoading}
        />
    </div>
  );
};

export default Managers;
